import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { callAction, isApiError, request, type ApiError } from './client'
import { t } from '@/i18n'
import type { ActionId, In, Out } from './generated/types'

export interface JSONSchema {
  type?: string | string[]
  properties?: Record<string, JSONSchema>
  required?: string[]
  items?: JSONSchema
  description?: string
  enum?: unknown[]
  [k: string]: unknown
}

export interface ActionManifest {
  id: string
  tool: string
  title: string
  description: string
  group: string
  audience: string
  permission: string
  read_only: boolean
  destructive: boolean
  idempotent: boolean
  open_world: boolean
  input_schema?: JSONSchema
  output_schema?: JSONSchema
}

/** Availability of an action for the current user. */
export type Availability = 'loading' | 'ok' | 'missing' | 'forbidden'

export const manifestKey = ['meta', 'actions'] as const
export const manifestAllKey = ['meta', 'actions', 'all'] as const

/** Actions the current user may call. */
export function useManifest() {
  return useQuery({
    queryKey: manifestKey,
    queryFn: () => request<ActionManifest[]>('GET', '/v1/meta/actions'),
    staleTime: 5 * 60_000,
  })
}

/** All actions registered on the server (to distinguish "not deployed" from "no permission"). */
export function useManifestAll() {
  return useQuery({
    queryKey: manifestAllKey,
    queryFn: () => request<ActionManifest[]>('GET', '/v1/meta/actions?all=1'),
    staleTime: 5 * 60_000,
  })
}

export function availabilityOf(
  id: string,
  allowed: ActionManifest[] | undefined,
  all: ActionManifest[] | undefined,
): Availability {
  if (!allowed || !all) return 'loading'
  if (allowed.some((a) => a.id === id)) return 'ok'
  if (all.some((a) => a.id === id)) return 'forbidden'
  return 'missing'
}

export function useAvailability(id: string): Availability {
  const allowed = useManifest().data
  const all = useManifestAll().data
  return availabilityOf(id, allowed, all)
}

/** True when the user may call the action (manifest-based; hides buttons the API would reject). */
export function useCan(id: string): boolean {
  return useAvailability(id) === 'ok'
}

export function actionKey(id: string, input?: unknown) {
  return input === undefined ? (['action', id] as const) : (['action', id, input] as const)
}

function shouldRetry(count: number, err: unknown) {
  if (isApiError(err) && err.status >= 400 && err.status < 500) return false
  return count < 2
}

export type ActionQueryResult<T> = UseQueryResult<T, ApiError> & { availability: Availability }

/**
 * Read an action as a query. The query is skipped when the manifest says the action
 * is not deployed or not permitted; `availability` reports why.
 */
export function useActionQuery<TOut, TIn = unknown>(
  id: string,
  input?: TIn,
  options: Omit<UseQueryOptions<TOut, ApiError>, 'queryKey' | 'queryFn'> = {},
): ActionQueryResult<TOut> {
  const avail = useAvailability(id)
  const q = useQuery<TOut, ApiError>({
    queryKey: actionKey(id, input ?? {}),
    queryFn: ({ signal }) => callAction<TOut>(id, input ?? {}, { signal }),
    retry: shouldRetry,
    ...options,
    enabled: avail === 'ok' && (options.enabled ?? true),
  })
  let availability = avail
  if (q.error?.isNotFoundAction) availability = 'missing'
  else if (q.error?.isForbidden) availability = 'forbidden'
  return Object.assign(q, { availability })
}

/**
 * Normalise list responses: planned actions may return a bare array or an envelope
 * ({items}, {results}, {rows} …). Returns [] for anything else.
 */
export function asList<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  if (d && typeof d === 'object') {
    const o = d as Record<string, unknown>
    for (const k of ['items', 'results', 'rows', 'data']) if (Array.isArray(o[k])) return o[k] as T[]
    for (const v of Object.values(o)) if (Array.isArray(v)) return v as T[]
  }
  return []
}

/** Invalidate all cached reads of a domain (e.g. "tasks" → tasks.list, tasks.get …). */
export function invalidateDomain(qc: QueryClient, ...domains: string[]) {
  return qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey
      return k[0] === 'action' && typeof k[1] === 'string' && domains.some((d) => (k[1] as string).startsWith(d + '.'))
    },
  })
}

export interface UseActionOptions<TOut, TIn> {
  /** Extra domains to invalidate on success (the action's own domain is always invalidated). */
  invalidate?: string[]
  onSuccess?: (data: TOut, input: TIn) => void
  /** Show a toast on success. */
  successMessage?: string | ((data: TOut) => string)
  /** When true, 422 validation errors with field errors are not toasted (the form shows them). */
  formErrors?: boolean
}

/** Human-readable message for an API error. */
export function errorMessage(err: unknown): string {
  if (!isApiError(err)) return t('error.generic')
  if (err.isNotFoundAction) return t('error.moduleMissing')
  // Specific codes (e.g. users.access_privileged is a 403) explain more than the generic text.
  const known = KNOWN_ERRORS[err.code]
  if (known) return t(known)
  if (err.isForbidden) return t('error.forbidden')
  if (err.status === 0) return t('error.network')
  if (err.status === 429) return t('error.rateLimited')
  return err.detail || t('error.generic')
}

const KNOWN_ERRORS: Record<string, Parameters<typeof t>[0]> = {
  'users.last_admin': 'error.lastAdmin',
  'user.email_taken': 'error.emailTaken',
  'auth.locked': 'error.locked',
  'invite.invalid': 'error.inviteInvalid',
  'password.policy': 'error.passwordPolicy',
  'password.current_invalid': 'error.currentPassword',
  'totp.invalid': 'error.totpInvalid',
  'auth.totp_invalid': 'error.totpInvalid',
  'users.access_self': 'users.accessSelf',
  'users.access_super_admin': 'users.accessSuperAdmin',
  'users.access_privileged': 'users.accessPrivileged',
}

/** Central error reporting: 401 is handled globally; 403 and others toast. */
export function reportError(err: unknown, opts: { formErrors?: boolean } = {}) {
  if (isApiError(err)) {
    if (err.isUnauthorized) return
    if (opts.formErrors && err.isValidation && Object.keys(err.fields).length > 0) return
  }
  toast.error(errorMessage(err))
}

/** Call an action as a mutation with central error handling and domain invalidation. */
export function useAction<TIn = unknown, TOut = unknown>(id: string, opts: UseActionOptions<TOut, TIn> = {}) {
  const qc = useQueryClient()
  const domain = id.split('.')[0]
  return useMutation<TOut, ApiError, TIn>({
    mutationKey: ['action', id],
    mutationFn: (input: TIn) => callAction<TOut>(id, input),
    onSuccess: (data, input) => {
      void invalidateDomain(qc, domain, ...(opts.invalidate ?? []))
      if (opts.successMessage) {
        toast.success(typeof opts.successMessage === 'function' ? opts.successMessage(data) : opts.successMessage)
      }
      opts.onSuccess?.(data, input)
    },
    onError: (err) => reportError(err, { formErrors: opts.formErrors }),
  })
}

// ---------------------------------------------------------------------------
// Typed wrappers over the generated contract (src/api/generated/types.ts).
// ---------------------------------------------------------------------------

/** Read a registered action with input/output types from the manifest snapshot. */
export function useQ<Id extends ActionId>(
  id: Id,
  input: In<Id>,
  options: Omit<UseQueryOptions<Out<Id>, ApiError>, 'queryKey' | 'queryFn'> = {},
): ActionQueryResult<Out<Id>> {
  return useActionQuery<Out<Id>, In<Id>>(id, input, options)
}

/** Mutation over a registered action, typed from the manifest snapshot. */
export function useA<Id extends ActionId>(id: Id, opts: UseActionOptions<Out<Id>, In<Id>> = {}) {
  return useAction<In<Id>, Out<Id>>(id, opts)
}

/** Typed one-off call (outside React Query). */
export function call<Id extends ActionId>(id: Id, input: In<Id>): Promise<Out<Id>> {
  return callAction<Out<Id>, In<Id>>(id, input)
}

/** `items` of a list envelope, never null. */
export function itemsOf<T>(d: { items?: T[] | null } | null | undefined): T[] {
  return d?.items ?? []
}
